import { DeviceEutGroup, Examination, Project } from "graphql/types"
import { isEutGroup } from "store/typeGuards"
import { v4 } from "uuid"
import { getOperatingModes } from "./getOperatingModes"

// Type for grouped devices by examination and test setup
export type GroupedDevices = {
  examination: Examination
  testSetups: TestSetup[]
}

// Type for a test setup (group of examinations with same name and norm.id)
export type TestSetup = {
  examinations: Examination[]
  devices: DeviceEutGroup[]
}

// Type for the result of grouping devices into test setups
export type TestSetupGroupingResult = {
  groupedDevices: GroupedDevices[]
  devicesWithoutExaminations: DeviceEutGroup[]
}

/**
 * Groups devices into test setups based on their examinations
 *
 * This function takes a project and groups all EUT_GROUP devices into test setups
 * based on their examinations. Devices with the same examination name and norm ID
 * are grouped together, and devices without examinations are separated.
 *
 * @param project - The project containing devices
 * @returns Object containing grouped devices and devices without examinations
 *
 * @example
 * ```typescript
 * const { groupedDevices, devicesWithoutExaminations } = groupDevicesIntoTestSetups(project)
 *
 *  groupedDevices contains arrays of test setups for each examination
 * groupedDevices.forEach(group => {
 *   console.log(`Examination: ${group.examination.name}`)
 *   group.testSetups.forEach((testSetup, index) => {
 *     console.log(`TestSetup ${index + 1}: ${testSetup.devices.length} devices`)
 *   })
 * })
 *
 *  devicesWithoutExaminations contains devices that have no examinations
 * console.log(`Devices without exams: ${devicesWithoutExaminations.length}`)
 * ```
 */
export const groupDevicesIntoTestSetups = (project: Project): TestSetupGroupingResult => {
  // Filter devices to only include EUT_GROUP types
  const eutGroups = project.devices.filter((device) => isEutGroup(device)) as DeviceEutGroup[]

  // Create a map to group devices by examination name and norm id
  const groupedMap = new Map<string, GroupedDevices>()
  const devicesWithoutExams: DeviceEutGroup[] = []

  eutGroups.forEach((device) => {
    // Check if device has any examinations
    if (device.examinations.length === 0) {
      devicesWithoutExams.push(device)
      return
    }

    device.examinations.forEach((examination) => {
      // Create key based on examination name and norm id for card grouping
      const cardKey = `${examination.name}_${examination.norm.id}`
      if (!groupedMap.has(cardKey)) {
        groupedMap.set(cardKey, {
          examination: examination,
          testSetups: [],
        })
      }

      const group = groupedMap.get(cardKey)!

      // Find or create test setup for this specific examination id
      let testSetup = group.testSetups.find((ts) => ts.examinations[0]?.id === examination.id)

      if (!testSetup) {
        testSetup = {
          examinations: [examination],
          devices: [],
        }
        group.testSetups.push(testSetup)
      }

      // Add device to test setup if not already present
      if (!testSetup.devices.find((d) => d.id === device.id)) {
        testSetup.devices.push(device)
      }
    })
  })

  return {
    groupedDevices: Array.from(groupedMap.values()),
    devicesWithoutExaminations: devicesWithoutExams,
  }
}

/**
 * Creates a new examination for a test setup with a new ID
 * @param baseExamination - The base examination to copy from
 * @param device - The device for which to create the examination
 * @returns New examination with new ID and preserved operating modes from base examination
 */
export const createNewExamination = (
  baseExamination: Examination,
  device: DeviceEutGroup
): Examination => {
  // Get operating modes from the device's DeviceDataGroup
  const deviceOperatingModes = getOperatingModes(device)
  return {
    ...baseExamination,
    id: v4(),
    operatingModes: deviceOperatingModes, // Assign operating modes from device's DeviceDataGroup
    salesQuote: "", // Reset salesQuote when device is moved
  }
}

/**
 * Sorts grouped devices by examination name and norm name
 * @param groupedDevices - Array of grouped devices
 * @returns Sorted array of grouped devices
 */
export const sortGroupedDevices = (groupedDevices: GroupedDevices[]): GroupedDevices[] => {
  return groupedDevices.sort((a, b) => {
    // First sort by examination name
    const examComparison = a.examination.name.localeCompare(b.examination.name)
    if (examComparison !== 0) {
      return examComparison
    }
    // Then sort by norm name
    return a.examination.norm.name.localeCompare(b.examination.norm.name)
  })
}

/**
 * Checks if a device exists in any test setup across all examinations
 * @param deviceId - The ID of the device to check
 * @param groupedDevices - Array of grouped devices
 * @returns True if device exists in any test setup, false otherwise
 */
export const deviceExistsInAnyTestSetup = (
  deviceId: string,
  groupedDevices: GroupedDevices[]
): boolean => {
  for (const group of groupedDevices) {
    for (const testSetup of group.testSetups) {
      if (testSetup.devices.find((d) => d.id === deviceId)) {
        return true
      }
    }
  }
  return false
}

/**
 * Moves a device from one test setup to another within the same examination
 * @param sourceTestSetup - The source test setup
 * @param destinationTestSetup - The destination test setup
 * @param deviceId - The ID of the device to move
 * @returns Object with updated source and destination test setups
 */
export const moveDeviceBetweenTestSetups = (
  sourceTestSetup: TestSetup,
  destinationTestSetup: TestSetup,
  deviceId: string
): { sourceTestSetup: TestSetup; destinationTestSetup: TestSetup } => {
  const deviceToMove = sourceTestSetup.devices.find((d) => d.id === deviceId)
  if (!deviceToMove) {
    throw new Error(`Device with ID ${deviceId} not found in source test setup`)
  }

  // Remove device from source and reset salesQuote
  const updatedSourceTestSetup: TestSetup = {
    ...sourceTestSetup,
    devices: sourceTestSetup.devices.filter((d) => d.id !== deviceId),
    examinations: sourceTestSetup.examinations.map((exam) => ({
      ...exam,
      salesQuote: "", // Reset salesQuote when device is removed
    })),
  }

  // Add device to destination and reset salesQuote with new ID
  const newDestID = v4()
  // Get operating modes from the device's DeviceDataGroup
  const deviceOperatingModes = getOperatingModes(deviceToMove)
  const updatedDestinationTestSetup: TestSetup = {
    ...destinationTestSetup,
    devices: [...destinationTestSetup.devices, deviceToMove],
    examinations: destinationTestSetup.examinations.map((exam) => ({
      ...exam,
      id: newDestID, // All examinations get the same new ID
      salesQuote: "", // Reset salesQuote when device is added
      operatingModes: deviceOperatingModes, // Assign operating modes from device's DeviceDataGroup
    })),
  }

  return {
    sourceTestSetup: updatedSourceTestSetup,
    destinationTestSetup: updatedDestinationTestSetup,
  }
}

/**
 * Creates a new test setup with a single device
 * @param baseExamination - The base examination to use
 * @param device - The device to add to the new test setup
 * @returns New test setup with the device
 */
export const createNewTestSetup = (
  baseExamination: Examination,
  device: DeviceEutGroup
): TestSetup => {
  const newExamination = createNewExamination(baseExamination, device)

  return {
    examinations: [newExamination],
    devices: [device],
  }
}

/**
 * Removes a device from a test setup
 * @param testSetup - The test setup to remove the device from
 * @param deviceId - The ID of the device to remove
 * @returns Updated test setup without the device
 */
export const removeDeviceFromTestSetup = (testSetup: TestSetup, deviceId: string): TestSetup => {
  const newID = v4()
  return {
    ...testSetup,
    devices: testSetup.devices.filter((d) => d.id !== deviceId),
    examinations: testSetup.examinations.map((exam) => ({
      ...exam,
      id: newID, // All examinations get the same new ID
      salesQuote: "", // Reset salesQuote when device is removed
    })),
  }
}

/**
 * Utility function to get all devices from grouped test setups
 * @param groupedDevices - Array of grouped devices
 * @returns Array of all devices from all test setups
 */
export const getAllDevicesFromTestSetups = (groupedDevices: GroupedDevices[]): DeviceEutGroup[] => {
  const devices: DeviceEutGroup[] = []

  groupedDevices.forEach((group) => {
    group.testSetups.forEach((testSetup) => {
      testSetup.devices.forEach((device) => {
        if (!devices.find((d) => d.id === device.id)) {
          devices.push(device)
        }
      })
    })
  })

  return devices
}

/**
 * Utility function to get all examinations from grouped test setups
 * @param groupedDevices - Array of grouped devices
 * @returns Array of all examinations from all test setups
 */
export const getAllExaminationsFromTestSetups = (
  groupedDevices: GroupedDevices[]
): Examination[] => {
  const examinations: Examination[] = []

  groupedDevices.forEach((group) => {
    group.testSetups.forEach((testSetup) => {
      testSetup.examinations.forEach((examination) => {
        if (!examinations.find((e) => e.id === examination.id)) {
          examinations.push(examination)
        }
      })
    })
  })

  return examinations
}
